import React, { useEffect } from 'react';
import _, { isNumber } from 'lodash';
import {
  Box,
  Button,
  Divider,
  Paper,
  Tab,
  Tabs,
  Theme,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import {
  Column,
  Table,
  TableHeaderRowProps,
  TableRowProps,
} from 'react-virtualized';
import { makeStyles } from '@material-ui/core/styles';
import { v4 } from 'uuid';
import classNames from 'classnames';
import ReactResizeDetector from 'react-resize-detector';
import styles from './ResultDetailsBody.styles';
import {
  FormInfo,
  OpenAttachPopupArgs,
  ResultDetailsDispatchFunc,
  ResultDetailsInfo,
  ResultInfo,
  ResultParamInfo,
  SelectGroupTabArgs,
} from '../types';
import ProgressMask from '../../../../components/progressMask';
import NetworkState from '../../../../core/types/network';
import config from '../../../../config';

const useStyles = makeStyles(styles);

type Props = {
  resultDetails?: ResultDetailsInfo;
  selectGroupTab: ResultDetailsDispatchFunc<SelectGroupTabArgs>;
  groupSelected: FormInfo;
  openAttachListPopup: ResultDetailsDispatchFunc<OpenAttachPopupArgs>;
  networkStates: {
    resultDetailsRequest: NetworkState;
    loginSessionRequest: NetworkState;
  };
};

const ResultDetailsBody: React.FC<Props> = ({
  resultDetails,
  selectGroupTab,
  groupSelected,
  openAttachListPopup,
  networkStates: {
    resultDetailsRequest,
    loginSessionRequest,
  },
}: Props) => {
  const classes = useStyles();
  const theme = useTheme();

  const isMediumScreen = useMediaQuery((themeS: Theme) => themeS.breakpoints.up('sm'));

  const forms = resultDetails?.resultInfo.map((val) => val.form);
  const groupForm = _.uniqBy(forms, 'formCode');
  const tabIndex = Math.max(groupForm.indexOf(groupSelected), 0);

  useEffect(() => {
    if (groupSelected && groupSelected.formCode === ''
      && resultDetails?.resultInfo) {
      selectGroupTab({ tab: groupForm[0] });
    }
  },
  [
    groupForm,
    groupSelected,
    resultDetails,
    resultDetailsRequest,
    selectGroupTab,
  ]);

  const groupTest = resultDetails?.resultInfo.filter(
    (value: ResultInfo) => value.form.formCode === groupSelected.formCode,
  );

  const groupParams = groupTest?.flatMap((value) => value.params);
  const paramList = groupParams || [];

  const headerRowRenderer = ({
    columns,
    style,
  }: TableHeaderRowProps) => (
    <Box
      className={classes.tableHeaderRow}
      role="row"
      style={style}
    >
      {columns}
    </Box>
  );

  const rowRenderer = ({
    key,
    columns,
    style,
    index,
    rowData,
  }: TableRowProps) => (
    <Box
      key={key}
      className={
        classNames(
          index % 2 === 0 ? classes.tableEvenRow : classes.tableOddRow,
          classes.tableHover,
          (rowData as ResultParamInfo).paramType === 'head' && classes.tableCellHead,
        )
      }
      role="row"
      style={style}
    >
      {columns}
    </Box>
  );

  const flagColor = (resultParam: ResultParamInfo) => {
    if (resultParam.rangeType === 'word' && resultParam.rangeNormal !== '') {
      if (resultParam.result !== resultParam.rangeNormal) {
        return classNames(
          classes.tableCellResult,
          classes.redText,
        );
      }
    }

    if (resultParam.rangeType === 'digit'
    && resultParam.rangeLow !== undefined
    && resultParam.rangeHigh !== undefined
    && resultParam.result !== undefined
    && isNumber(parseFloat(resultParam.result.replace('<', '').replace('>', '').replace(',', '')))) {
      const result = resultParam.result.replace('<', '').replace('>', '').replace(',', '');
      if ((result as unknown as number) > resultParam.rangeHigh) {
        return classNames(
          classes.tableCellResult,
          classes.redText,
        );
      }

      if ((result as unknown as number) < resultParam.rangeLow) {
        return classNames(
          classes.tableCellResult,
          classes.blueText,
        );
      }
    }

    return classNames(
      classes.tableCellResult,
    );
  };

  const flagColorLR = (resultParam: ResultParamInfo) => {
    if (resultParam.rangeType === 'word' && resultParam.rangeNormal !== '') {
      if (resultParam.lastResult !== resultParam.rangeNormal) {
        return classNames(
          classes.tableCellResult,
          classes.redText,
        );
      }
    }

    if (resultParam.rangeType === 'digit'
    && resultParam.rangeLow !== undefined
    && resultParam.rangeHigh !== undefined
    && resultParam.lastResult !== undefined
    && isNumber(resultParam.lastResult.replace('<', '').replace('>', '').replace(',', ''))) {
      const lastResult = resultParam.lastResult.replace('<', '').replace('>', '').replace(',', '');
      if ((lastResult as unknown as number) > resultParam.rangeHigh) {
        return classNames(
          classes.redText,
        );
      }

      if ((lastResult as unknown as number) < resultParam.rangeLow) {
        return classNames(
          classes.blueText,
        );
      }
    }

    return null;
  };

  const flagText = (resultParam: ResultParamInfo) => {
    if (resultParam.rangeType === 'word' && resultParam.rangeNormal !== '') {
      if (resultParam.result !== resultParam.rangeNormal) {
        return '!';
      }
    }

    if (resultParam.rangeType === 'digit'
    && resultParam.rangeLow !== undefined
    && resultParam.rangeHigh !== undefined
    && resultParam.result !== undefined
    && isNumber(parseFloat(resultParam.result.replace('<', '').replace('>', '').replace(',', '')))) {
      const result = resultParam.result.replace('<', '').replace('>', '').replace(',', '');
      if ((result as unknown as number) > resultParam.rangeHigh) {
        return 'H';
      }

      if ((result as unknown as number) < resultParam.rangeLow) {
        return 'L';
      }
    }

    return '';
  };

  return (
    <Box className={classes.root}>
      <Paper classes={{ root: classes.details }} square>
        <Tabs
          indicatorColor="secondary"
          textColor="primary"
          value={tabIndex}
          scrollButtons="on"
          variant="scrollable"
          onChange={(_v, index) => {
            const tab = groupForm[index];
            selectGroupTab({ tab });
          }}
        >
          {groupForm.map((groupName) => (
            <Tab key={v4()} label={groupName.formName} classes={{ root: classes.tabItem }} />
          ))}
        </Tabs>
        <Divider />

        {resultDetailsRequest.isRequesting && (
          <ProgressMask />
        )}
        {!resultDetailsRequest.isRequesting && paramList && (
          <>
            {paramList.length >= 1 ? (
              <ReactResizeDetector handleHeight>
                {({ width, height }) => (
                  <Table
                    height={height || 0}
                    width={width || 0}
                    rowCount={paramList.length}
                    rowGetter={({ index }) => paramList[index]}
                    rowHeight={theme.spacing(4)}
                    headerHeight={theme.spacing(4)}
                    gridClassName={classes.tableGrid}
                    headerRowRenderer={headerRowRenderer}
                    headerClassName={classNames(
                      classes.tableHeaderCell,
                      classes.tableHeaderCellCenter,
                    )}
                    rowRenderer={rowRenderer}
                    className={classes.table}
                  >
                    <Column
                      label="Test / Param"
                      cellDataGetter={({ rowData }) => (rowData as ResultParamInfo).param}
                      dataKey="param"
                      width={200}
                      className={classNames(classes.tableCell)}
                      cellRenderer={({ cellData }) => (
                        <Tooltip title={cellData || ''}>
                          <Box className={classes.innerCellText}>
                            {cellData}
                          </Box>
                        </Tooltip>
                      )}
                    />
                    <Column
                      label="Result"
                      cellDataGetter={({ rowData }) => (rowData as ResultParamInfo).result}
                      dataKey="result"
                      width={190}
                      className={
                        classNames(
                          classes.tableCell,
                          classes.tableCellCenter,
                        )
                      }
                      cellRenderer={({ rowData, cellData }) => (
                        <Tooltip title={cellData || ''}>
                          {(rowData as ResultParamInfo).isAttach ? (
                            <Button
                              color="primary"
                              classes={{ root: classes.linkButton }}
                              variant="text"
                              size="small"
                              onClick={() => openAttachListPopup({
                                resultDetailId: (rowData as ResultParamInfo).resultDetailId,
                              })}
                            >
                              {cellData}
                            </Button>
                          ) : (
                            <Box className={
                              classNames(classes.innerCellText, flagColor(rowData))
                            }
                            >
                              {cellData}
                            </Box>
                          )}
                        </Tooltip>
                      )}
                    />
                    <Column
                      label="F"
                      cellDataGetter={({ rowData }) => (rowData as ResultParamInfo).result}
                      dataKey="flag"
                      width={40}
                      className={
                        classNames(
                          classes.tableCell,
                          classes.tableCellCenter,
                        )
                      }
                      cellRenderer={({ rowData, cellData }) => (
                        <Tooltip title={cellData || ''}>
                          <Box className={
                              classNames(classes.innerCellText, flagColor(rowData))
                            }
                          >
                            {flagText(rowData)}
                          </Box>
                        </Tooltip>
                      )}
                    />
                    <Column
                      label="Ref.Interval"
                      cellDataGetter={({ rowData }) => (rowData as ResultParamInfo).rangeWordingEn}
                      dataKey="rangeWordingEN"
                      width={200}
                      className={classNames(classes.tableCell, classes.tableCellCenter)}
                      cellRenderer={({ cellData }) => (
                        <Tooltip title={cellData || ''}>
                          <Box className={classes.innerCellText}>
                            {cellData}
                          </Box>
                        </Tooltip>
                      )}
                    />
                    <Column
                      label="Unit"
                      cellDataGetter={({ rowData }) => (rowData as ResultParamInfo).unit}
                      dataKey="unit"
                      width={120}
                      className={classNames(classes.tableCell, classes.tableCellCenter)}
                    />
                    {isMediumScreen && (
                      <Column
                        label="Last result"
                        cellDataGetter={({ rowData }) => (rowData as ResultParamInfo).lastResult}
                        dataKey="lastResult"
                        width={190}
                        className={classNames(classes.tableCell, classes.tableCellCenter)}
                        cellRenderer={({ rowData, cellData }) => (
                          <Tooltip title={cellData || ''}>
                            <Box className={
                              classNames(classes.innerCellText, flagColorLR(rowData))
                            }
                            >
                              {cellData}
                            </Box>
                          </Tooltip>
                        )}
                      />
                    )}
                  </Table>
                )}
              </ReactResizeDetector>
            ) : (
              !loginSessionRequest.isRequesting && (
                <Box className={classes.centerScreen}>
                  {resultDetails?.requestInfo.status === 'cancelled' ? (
                    <>
                      <img
                        src={`${config.routerBase}/canceled.png`}
                        alt="In process "
                        className={classes.centerImage}
                      />
                      <Typography variant="h4" color="inherit">Request is canceled</Typography>
                    </>
                  ) : (
                    <>
                      <img
                        src={`${config.routerBase}/lab_in_process.png`}
                        alt="In process "
                        className={classes.centerImage}
                      />
                      <Typography variant="h4" color="inherit">Lab in process</Typography>
                    </>
                  )}
                </Box>
              )
            )}
          </>
        )}
      </Paper>
    </Box>
  );
};

export default ResultDetailsBody;
